/**
 * 단위 체계 , 프로그램이 지원하는 입력 단위와 차원.
 *
 * 과업지시서: "프로그램에서 지원하는 입력항목과 단위체계 안에서" 사용자 정의 설비를 만들 수 있어야 하고,
 * "단위 오류" 는 입력 검증에서 잡아야 한다. 그래서 단위는 자유 문자열이 아니라 여기 목록에 있는 것만 받는다.
 *
 * 차원(dimension)이 같은 단위끼리만 환산한다 , kWh↔MJ 는 되고 kWh↔kg 는 단위 오류다.
 */
export const DIMENSIONS = ["energy", "mass", "volume", "gasVolume", "distance", "time", "ratio", "count"] as const;
export type Dimension = (typeof DIMENSIONS)[number];

export interface UnitDef {
  /** 표시,저장에 쓰는 기호 */
  symbol: string;
  dimension: Dimension;
  /** 같은 차원의 기준 단위로 가는 곱셈 계수 (기준 단위는 factor 1) */
  toBase: number;
  label: string;
}

/** 기준 단위: energy=kWh, mass=kg, volume=L, gasVolume=Nm3, distance=km, time=h */
export const UNITS: Record<string, UnitDef> = {
  kWh: { symbol: "kWh", dimension: "energy", toBase: 1, label: "킬로와트시" },
  MWh: { symbol: "MWh", dimension: "energy", toBase: 1000, label: "메가와트시" },
  MJ: { symbol: "MJ", dimension: "energy", toBase: 1 / 3.6, label: "메가줄" },
  GJ: { symbol: "GJ", dimension: "energy", toBase: 1000 / 3.6, label: "기가줄" },
  kg: { symbol: "kg", dimension: "mass", toBase: 1, label: "킬로그램" },
  g: { symbol: "g", dimension: "mass", toBase: 0.001, label: "그램" },
  t: { symbol: "t", dimension: "mass", toBase: 1000, label: "톤" },
  L: { symbol: "L", dimension: "volume", toBase: 1, label: "리터" },
  m3: { symbol: "m3", dimension: "volume", toBase: 1000, label: "세제곱미터" },
  Nm3: { symbol: "Nm3", dimension: "gasVolume", toBase: 1, label: "표준세제곱미터" },
  km: { symbol: "km", dimension: "distance", toBase: 1, label: "킬로미터" },
  m: { symbol: "m", dimension: "distance", toBase: 0.001, label: "미터" },
  h: { symbol: "h", dimension: "time", toBase: 1, label: "시간" },
  "%": { symbol: "%", dimension: "ratio", toBase: 0.01, label: "퍼센트" },
  ratio: { symbol: "ratio", dimension: "ratio", toBase: 1, label: "비율(0~1)" },
  ea: { symbol: "ea", dimension: "count", toBase: 1, label: "개" },
};

export const UNIT_SYMBOLS = Object.keys(UNITS) as [string, ...string[]];

export function unitOf(symbol: string): UnitDef | undefined {
  return UNITS[symbol];
}

/** 같은 차원이면 환산 계수(from → to)를, 아니면 null 을 돌려준다 */
export function conversionFactor(from: string, to: string): number | null {
  const a = UNITS[from];
  const b = UNITS[to];
  if (!a || !b || a.dimension !== b.dimension) return null;
  return a.toBase / b.toBase;
}

/** "kgCO2eq/kWh" 같은 배출계수 단위에서 분모 단위를 뽑는다 */
export function denominatorUnit(efUnit: string): string | null {
  const m = /^kgCO2eq\/(.+)$/.exec(efUnit.trim());
  return m?.[1] ?? null;
}
